/**
 * TEMPORARY boot instrumentation — ticket DCM001CMG01-17401 (frozen order screen).
 *
 * REMOVE once the freeze is diagnosed. Every call is best-effort and can never
 * throw: a diagnostic must not become a second failure.
 *
 * Why the payload goes over the network at all: the freeze only reproduces on
 * other people's machines and what comes back are HAR captures, where console
 * output is lost. So the trace is flushed as POST bodies that the HAR records
 * verbatim.
 *
 * Two things this deliberately does NOT rely on:
 *
 * - HAR timestamps of the beacons themselves. The first version used <img> GETs;
 *   images are low priority and Chrome held them ~1s in the queue, so their
 *   position in the capture did not match when they were recorded and ordering
 *   them against real API calls was guesswork. Every event now carries
 *   `t` = performance.now(), which shares its timebase with
 *   PerformanceResourceTiming — the same clock the HAR is built from.
 *
 * - Cross-referencing by hand. Each flush embeds the browser's own resource
 *   timings for the requests under investigation (`res`), so "when did
 *   mylegalentities actually start and finish" travels inside the payload,
 *   already on the same clock as the events.
 *
 * Reading a capture: filter by `__trace`, read the POST bodies in order and
 * concatenate their `ev` arrays. Steps come in start/end pairs, so a `:start`
 * with no matching `:end` is where execution stopped; `err:*` carries the
 * message. `window.__bootTrace` / `window.__bootTraceDump()` give the same data
 * live on the affected machine.
 */

// Enough for a boot; the cap stops a loop from flooding the network tab.
const MAX_EVENTS = 200;
const MAX_DETAIL_CHARS = 300;
// Batch window: long enough to coalesce a burst, short enough that a freeze
// still ships what it managed to record.
const FLUSH_DEBOUNCE_MS = 700;
// Requests whose browser-measured timings ride along with every flush.
const TRACKED_RESOURCES = /mylegalentities|myshipmentlocations|countryconfig|applications\/menu|ce\/parameters/;

export interface BootTraceEvent {
  n: number;
  /** performance.now() — same timebase as PerformanceResourceTiming. */
  t: number;
  step: string;
  detail?: string;
}

const events: BootTraceEvent[] = [];
const pendingImages: HTMLImageElement[] = [];
let seq = 0;
let flushedUpTo = 0;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let trapsInstalled = false;

export function bootTrace(step: string, detail?: string): void {
  try {
    if (seq >= MAX_EVENTS) {
      return;
    }
    events.push({
      n: ++seq,
      t: Math.round(now()),
      step,
      detail: detail ? String(detail).slice(0, MAX_DETAIL_CHARS) : undefined,
    });
    (window as unknown as { __bootTrace?: BootTraceEvent[] }).__bootTrace = events;
    scheduleFlush();
  } catch {
    // Diagnostics never break the app.
  }
}

/**
 * Formats anything that lands in a catch block (Error, HttpErrorResponse,
 * string, undefined) into a single line worth putting in a payload.
 */
export function describeError(error: unknown): string {
  try {
    if (!error) {
      return String(error);
    }
    const err = error as { message?: string; name?: string; status?: number; stack?: string };
    const head = [err.name, err.message ?? String(error), err.status ? `status=${err.status}` : null]
      .filter(Boolean)
      .join(': ');
    // First stack frame is usually enough to identify the culprit.
    const frame = (err.stack || '').split('\n')[1]?.trim();
    return frame ? `${head} | ${frame}` : head;
  } catch {
    return 'unserializable-error';
  }
}

/** Catches anything thrown outside Angular's ErrorHandler (async callbacks, rejections). */
export function installBootTraceTraps(): void {
  try {
    if (trapsInstalled) {
      return;
    }
    trapsInstalled = true;
    window.addEventListener('error', (event) => {
      bootTrace('err:window', `${event.message} @ ${event.filename}:${event.lineno}`);
    });
    window.addEventListener('unhandledrejection', (event) => {
      bootTrace('err:promise', describeError((event as PromiseRejectionEvent).reason));
    });
    // A tab closed mid-freeze must not take the trace with it.
    window.addEventListener('pagehide', () => flush(true));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flush(true);
      }
    });
    (window as unknown as { __bootTraceDump?: () => string }).__bootTraceDump = () =>
      events.map((e) => `${e.n}\t${e.t}ms\t${e.step}${e.detail ? `\t${e.detail}` : ''}`).join('\n');
    bootTrace('env:xhr', describeXhrLayer());
    probeHttpLayer();
  } catch {
    // Diagnostics never break the app.
  }
}

/**
 * Who owns XMLHttpRequest by the time the app boots.
 *
 * The captures show responses arriving complete at the network layer while the
 * Angular observable never emits them — a fingerprint of an XHR wrapper that
 * swallows the callback. Both Zone.js and the Dynatrace RUM agent patch
 * XMLHttpRequest, and the agent is present in these captures, so record who
 * patched what instead of guessing.
 */
function describeXhrLayer(): string {
  try {
    const w = window as unknown as Record<string, unknown>;
    const isNative = (fn: unknown) => typeof fn === 'function' && /\[native code\]/.test(Function.prototype.toString.call(fn));
    const proto = XMLHttpRequest.prototype as unknown as Record<string, unknown>;
    // NOTE: bound functions and Proxies also stringify as [native code], so
    // "native" here means "not obviously wrapped", not "untouched".
    return [
      `send=${isNative(proto.send) ? 'native' : 'patched'}`,
      `open=${isNative(proto.open) ? 'native' : 'patched'}`,
      `ctor=${isNative(w.XMLHttpRequest) ? 'native' : 'patched'}`,
      `addEL=${isNative(proto.addEventListener) ? 'native' : 'patched'}`,
      // Zone.js keeps the untouched originals under these keys when it patches.
      `zonePatched=${!!(proto as { [k: string]: unknown })['__zone_symbol__send']}`,
      `dynatrace=${!!(w.dT_ || w.dtrum)}`,
      `dtAgent=${(w.dT_ as { version?: string })?.version ?? 'n/a'}`,
      // The Cookiebot workaround (src/app/shared/compat/xhr-cookiebot-shim.ts):
      // flag set by the shim + proof the own-property shadow is in place, so
      // every capture states whether that boot was shielded.
      `xhrShim=${(w as { __xhrCookiebotShim?: boolean }).__xhrCookiebotShim === true}`,
      `xhrOwnAddEL=${Object.prototype.hasOwnProperty.call(XMLHttpRequest.prototype, 'addEventListener')}`,
    ].join(' ');
  } catch {
    return 'unavailable';
  }
}

/**
 * Decides where HTTP responses are being lost, independently of Angular.
 *
 * The captures show every response arriving complete at the network layer while
 * no Angular observable ever emits — the app boots deaf. This fires one raw
 * XMLHttpRequest and one fetch() outside Angular entirely, so the next capture
 * separates three cases that look identical from inside the app:
 *
 *   xhr:* silent, fetch:ok      -> XMLHttpRequest itself is broken (wrapper/agent)
 *   xhr:load fires, fetch:ok    -> XHR works; the loss is in Angular/Zone above it
 *   both silent                 -> the whole page stopped being delivered responses
 *
 * Targets a URL that 404s on purpose: an HTTP 404 still fires `load`, which is
 * all we are measuring, and it touches nothing real.
 */
function probeHttpLayer(): void {
  try {
    const url = `${beaconUrl()}?probe=1&r=${Math.floor(Math.random() * 1e6)}`;

    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.onreadystatechange = () => bootTrace('probe:xhr:state', `readyState=${xhr.readyState}`);
    xhr.onload = () => bootTrace('probe:xhr:load', `status=${xhr.status}`);
    xhr.onerror = () => bootTrace('probe:xhr:error');
    xhr.ontimeout = () => bootTrace('probe:xhr:timeout');
    xhr.send();
    bootTrace('probe:xhr:sent');

    if (typeof fetch === 'function') {
      fetch(`${url}&via=fetch`)
        .then((response) => bootTrace('probe:fetch:ok', `status=${response.status}`))
        .catch((error) => bootTrace('probe:fetch:error', describeError(error)));
      bootTrace('probe:fetch:sent');
    }
  } catch (error) {
    bootTrace('probe:setup:error', describeError(error));
  }
}

function now(): number {
  return typeof performance !== 'undefined' && performance.now ? performance.now() : 0;
}

function scheduleFlush(): void {
  if (flushTimer) {
    return;
  }
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush(false);
  }, FLUSH_DEBOUNCE_MS);
}

function flush(final: boolean): void {
  try {
    const fresh = events.slice(flushedUpTo);
    if (!fresh.length && !final) {
      return;
    }
    flushedUpTo = events.length;
    const payload = JSON.stringify({
      // Wall-clock anchor so several captures can be lined up; `t` values are
      // page-relative and are what ordering should be based on.
      at: new Date().toISOString(),
      final,
      ev: fresh,
      res: resourceTimings(),
    });
    const url = beaconUrl();
    if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([payload], { type: 'text/plain' }))) {
      return;
    }
    // Fallback: a GET carrying as much as a URL can hold.
    const img = new Image();
    pendingImages.push(img);
    const done = () => {
      const at = pendingImages.indexOf(img);
      if (at !== -1) {
        pendingImages.splice(at, 1);
      }
    };
    img.onload = done;
    img.onerror = done; // 404 is the expected outcome.
    img.src = `${url}?p=${encodeURIComponent(payload.slice(0, 1800))}`;
  } catch {
    // Diagnostics never break the app.
  }
}

/**
 * The browser's own measurements for the requests under investigation, on the
 * same clock as the events above — this is what makes the capture readable
 * without guessing at HAR timestamps.
 */
function resourceTimings(): Array<Record<string, number | string>> {
  try {
    return performance
      .getEntriesByType('resource')
      .filter((entry) => TRACKED_RESOURCES.test(entry.name))
      .slice(-20)
      .map((entry) => {
        const r = entry as PerformanceResourceTiming;
        return {
          u: r.name.replace(/^https?:\/\/[^/]+/, '').slice(0, 90),
          start: Math.round(r.startTime),
          // 0 while the request is still in flight — exactly what we need to
          // tell "never answered" apart from "answered but never delivered".
          end: Math.round(r.responseEnd),
          dur: Math.round(r.duration),
          size: r.transferSize || 0,
        };
      });
  } catch {
    return [];
  }
}

function beaconUrl(): string {
  try {
    return new URL('assets/__trace.gif', document.baseURI).toString();
  } catch {
    return '/assets/__trace.gif';
  }
}
